import { X } from 'lucide-react'
import { type ReactNode, useEffect } from 'react'
import { cn } from '../../lib/utils'

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  size = 'md',
}: {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
}) {
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const sizes = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 animate-page-in">
      <button
        type="button"
        onClick={onClose}
        className="absolute inset-0 bg-zinc-950/80 backdrop-blur-md"
        aria-label="Chiudi"
      />
      <div
        className={cn(
          'relative w-full rounded-xl border border-zinc-800 bg-zinc-900/95 overflow-hidden animate-scale-in',
          'shadow-[0_24px_64px_-12px_rgba(0,0,0,0.7),0_0_0_1px_rgba(34,211,238,0.12)_inset,0_0_60px_-12px_rgba(34,211,238,0.25)]',
          sizes[size],
        )}
      >
        {/* Top cyan hairline */}
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-500/60 to-transparent"
        />
        <header className="flex items-start justify-between gap-4 px-5 py-4 border-b border-zinc-800/80 bg-zinc-900/60">
          <div>
            <h2 className="font-display text-[15px] font-semibold tracking-tight text-zinc-50">
              {title}
            </h2>
            {description && (
              <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800/70 rounded-md p-1 transition-colors"
            aria-label="Chiudi"
          >
            <X className="size-4" />
          </button>
        </header>
        <div className="max-h-[70vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  )
}
